import * as z from "zod";

/**
 * Single source of truth for the set of products visible in the admin
 * Onboardings → Merchants views (list tabs, detail product nav, status
 * dots, overview panels).
 *
 * Adding a new admin-visible product is a one-line change here — every
 * downstream consumer (SDK schemas, server handlers, URL state, components)
 * derives its view from this registry.
 *
 * Note: a product being in this registry only means it's admin-visible.
 * The merchant-facing onboarding flow, step definitions, and CRDB rows
 * are managed independently via `redo/onboarding/api/schema/initialize.ts`
 * and `onboarding-v2-static-step-totals.ts`.
 */
export const V2_ADMIN_PRODUCTS = [
  { id: "returns", label: "Returns" },
  { id: "order-editing", label: "Order Editing" },
  { id: "recover", label: "Recover" },
  { id: "chargebacks", label: "Chargebacks" },
] as const;

export type V2AdminProductEntry = (typeof V2_ADMIN_PRODUCTS)[number];
export type V2AdminProductId = V2AdminProductEntry["id"];

export const V2_ADMIN_PRODUCT_IDS: readonly V2AdminProductId[] =
  V2_ADMIN_PRODUCTS.map((p) => p.id);

/**
 * Named constants for use at comparison sites. Prefer
 * `V2_ADMIN_PRODUCT.RECOVER` over the bare `"recover"` string literal — the
 * `satisfies` assertion catches typos at compile time and enables IDE
 * find-references.
 */
export const V2_ADMIN_PRODUCT = {
  RETURNS: "returns",
  ORDER_EDITING: "order-editing",
  RECOVER: "recover",
  CHARGEBACKS: "chargebacks",
} as const satisfies Record<string, V2AdminProductId>;

export const V2AdminProductIdSchema = z.enum(
  V2_ADMIN_PRODUCT_IDS as unknown as [V2AdminProductId, ...V2AdminProductId[]],
);

export function getV2AdminProductEntry(
  productId: V2AdminProductId,
): V2AdminProductEntry {
  const entry = V2_ADMIN_PRODUCTS.find((p) => p.id === productId);
  if (!entry) {
    // Unreachable: the type system guarantees productId is in the registry.
    throw new Error(`Unknown V2 admin product: ${productId}`);
  }
  return entry;
}

export function isV2AdminProductId(value: string): value is V2AdminProductId {
  return (V2_ADMIN_PRODUCT_IDS as readonly string[]).includes(value);
}
